import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MemberBasic, Member } from '../models/member';
import {
  Container,
  VStack,
  Box,
  Heading,
  Text,
  HStack,
  Icon,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { FaIdCard } from 'react-icons/fa';
import { useAuth } from '../components/auth';
import Credential from "../components/credential";
import { getMember } from '../services/member-service';

// Helper function to convert Member to MemberBasic
const convertMemberToMemberBasic = (member: Member): MemberBasic => {
  return {
    id: member.id,
    firstName: member.firstName,
    lastName: member.lastName,
    documentNumber: member.documentNumber,
    memberNumber: member.memberNumber,
    membershipDate: member.membershipDate,
    type: member.memberType?.value || '',
    imagePath: member.imagePath
  };
};

function MyMembershipCredential(): JSX.Element {
  const auth = useAuth();
  const { memberId } = useParams<{ memberId?: string }>();
  const [member, setMember] = useState<MemberBasic | null>(auth?.user?.selectedMember as MemberBasic);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('green.100', 'green.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    if (!auth?.user?.selectedMember && memberId) {
      setLoading(true);
      setError(null);

      getMember(memberId)
        .then((memberData) => {
          if (memberData) {
            setMember(convertMemberToMemberBasic(memberData));
          } else {
            setError('No se pudo encontrar el socio especificado.');
          }
        })
        .catch((err) => {
          console.error('Error al recuperar datos del socio:', err);
          setError('Error al cargar los datos del socio.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [auth?.user?.selectedMember, memberId]);

  if (loading) {
    return (
      <Container maxW="container.xl" py={6}>
        <VStack spacing={6} align="center">
          <Spinner size="xl" color="green.500" />
          <Text>Cargando credencial...</Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  if (member == null) {
    return (
      <Container maxW="container.xl" py={6}>
        <Alert status="warning">
          <AlertIcon />
          No se encontraron datos del socio para mostrar la credencial.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        <Box
          bg={headerBg}
          p={6}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="sm"
        >
                     <HStack>
             <Icon as={FaIdCard} color="green.500" boxSize={6} />
             <Heading color={textColor}>Mi credencial de socio</Heading>
           </HStack>
          <Text mt={2} color={secondaryTextColor}>
            Esta es tu credencial digital de socio. Podés mostrarla en el club para identificarte.
          </Text>
        </Box>

        {/* Credential Card */}
        <Box
          bg={cardBg}
          p={8}
          borderRadius="lg"
          border="1px solid"
          borderColor={borderColor}
          boxShadow="lg"
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <Credential member={member}/>
        </Box>
      </VStack>
    </Container>
  );
}

export default MyMembershipCredential;
